import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
    LayoutDashboard,
    Package,
    ShoppingCart,
    BarChart3,
    LogOut,
    Menu,
    Truck,
    ClipboardCheck,
    Users,
    Receipt,
    Boxes,
    RotateCcw,
    Wallet,
    Globe,
    Sun,
    Moon,
    TrendingUp,
    TrendingDown
} from 'lucide-react';

// Role-based navigation configuration with translation keys
const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, labelKey: 'dashboard', roles: ['ADMIN', 'WAREHOUSE_STAFF', 'ORDER_CONFIRMATION', 'ACCOUNTANT', 'TYPE_A'] },
    { to: '/orders', icon: ShoppingCart, labelKey: 'orders', roles: ['ADMIN', 'ORDER_CONFIRMATION', 'TYPE_A'] },
    { to: '/products', icon: Package, labelKey: 'products', roles: ['ADMIN', 'WAREHOUSE_STAFF', 'TYPE_A'] },
    {
        to: '/inventory',
        icon: Boxes,
        labelKey: 'inventory',
        roles: ['ADMIN', 'WAREHOUSE_STAFF', 'TYPE_A'],
        children: [
            { to: '/buy-stock', icon: TrendingUp, labelKey: 'buyStock', roles: ['ADMIN', 'TYPE_A'] },
            { to: '/sell-stock', icon: TrendingDown, labelKey: 'sellStock', roles: ['ADMIN', 'ORDER_CONFIRMATION', 'TYPE_A'] },
            { to: '/return-stock', icon: RotateCcw, labelKey: 'Return Stock', roles: ['ADMIN', 'ORDER_CONFIRMATION', 'TYPE_A'] },
        ]
    },
    { to: '/returns', icon: RotateCcw, labelKey: 'returns', roles: ['ADMIN', 'WAREHOUSE_STAFF', 'TYPE_A'] },
    {
        to: '/expenses',
        icon: Receipt,
        labelKey: 'expenses',
        roles: ['ADMIN', 'ACCOUNTANT', 'TYPE_A'],
        children: [
            { to: '/expense-categories', icon: Receipt, labelKey: 'expenseCategoryManagement', roles: ['ADMIN', 'ACCOUNTANT'] },
        ]
    },
    { to: '/financial', icon: Wallet, labelKey: 'financial', roles: ['ADMIN', 'ACCOUNTANT', 'TYPE_A'] },
    { to: '/reports', icon: BarChart3, labelKey: 'reports', roles: ['ADMIN', 'ACCOUNTANT'] },
    { to: '/delivery-providers', icon: Truck, labelKey: 'deliveryProviders', roles: ['ADMIN'] },
    { to: '/users', icon: Users, labelKey: 'users', roles: ['ADMIN'] },
];

export function Layout({ children }) {
    const { user, logout } = useAuth();
    const { t, language, setLanguage, availableLanguages } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = React.useState(false);


    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const toggleLanguage = () => {
        const currentIndex = availableLanguages.findIndex(l => l.code === language);
        const nextIndex = (currentIndex + 1) % availableLanguages.length;
        setLanguage(availableLanguages[nextIndex].code);
    };

    return (
        <div className="min-h-screen flex">
            {/* Sidebar */}
            <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center gap-3 p-4 border-b">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <Package className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-semibold">Stock Management</h1>
                            <p className="text-xs text-muted-foreground">ERP System</p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-2">
                        {navItems
                            .filter(item => item.roles.includes(user?.role))
                            .map((item) => {
                                const Icon = item.icon;
                                const hasChildren = item.children && item.children.length > 0;
                                const hasVisibleChildren = hasChildren && item.children.some(child => child.roles.includes(user?.role));

                                return (
                                    <div key={item.to} className="space-y-1">
                                        <NavLink
                                            to={item.to}
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                                                    ? 'bg-primary text-primary-foreground font-medium'
                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                }`
                                            }
                                        >
                                            <Icon className="h-4 w-4" />
                                            {t(item.labelKey)}
                                        </NavLink>

                                        {hasVisibleChildren && (
                                            <div className="pl-6 space-y-1 border-l-2 border-muted ml-4 pt-1 pb-2">
                                                {item.children
                                                    .filter(child => child.roles.includes(user?.role))
                                                    .map(child => {
                                                        const ChildIcon = child.icon;
                                                        return (
                                                            <NavLink
                                                                key={child.to}
                                                                to={child.to}
                                                                onClick={() => setSidebarOpen(false)}
                                                                className={({ isActive }) =>
                                                                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                                                                        ? 'text-primary font-medium bg-primary/10'
                                                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                                                    }`
                                                                }
                                                            >
                                                                <ChildIcon className="h-4 w-4" />
                                                                {t(child.labelKey)}
                                                            </NavLink>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t">
                        {/* Theme & Language toggles */}
                        <div className="flex gap-2 mb-3">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1 justify-start"
                                onClick={toggleLanguage}
                            >
                                <Globe className="h-4 w-4 mr-2" />
                                {availableLanguages.find(l => l.code === language)?.flag}{' '}
                                {availableLanguages.find(l => l.code === language)?.name}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleTheme}
                                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                            >
                                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                            </Button>
                        </div>

                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium">
                                    {user?.name?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
                            <LogOut className="h-4 w-4 mr-2" />
                            {t('logout')}
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Mobile header */}
                <header className="lg:hidden flex items-center gap-4 p-4 border-b">
                    <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
                        <Menu className="h-5 w-5" />
                    </Button>
                    <h1 className="font-semibold">Stock Management</h1>
                </header>

                {/* Page content */}
                <main className="flex-1 p-6 overflow-auto">
                    {children}
                </main>
            </div>
        </div>
    );
}
